import { BadgeCheck, CalendarClock, ChevronRight, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { resourcesNav } from '@/components/px/nav'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  BADGES_CHROME,
  BADGES_COPY,
  BADGES_TABLE,
  BADGE_ROWS,
  EXPIRY_PANEL,
  LADDER,
  LADDER_MARKER,
  LEVELS,
  MATCH_PANEL,
  VERIFICATION_PANEL,
  levelLabel,
  type BadgeRow,
  type Level,
  type Segment,
} from '@/demo/content/badges'
import { cn } from '@/lib/utils'

/** Prose in which some words are real destinations (never a dead phrase). */
function Prose({ segments, className }: { segments: readonly Segment[]; className?: string }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'text' ? (
          <span key={index}>{segment.text}</span>
        ) : (
          <Link
            key={index}
            to={segment.href}
            className={cn('font-medium underline underline-offset-2 hover:no-underline', className)}
          >
            {segment.label}
          </Link>
        )
      )}
    </>
  )
}

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

/**
 * One rung. Every rung carries the people accent because the ladder is a fact
 * about a person; the rung one seeded person stands on today additionally
 * carries the lavender wash and says who is standing there.
 */
function LadderStep({ level, marked }: { level: Level; marked: boolean }) {
  return (
    <div
      className={cn(
        'flex h-full min-w-[10.5rem] flex-col rounded-lg border border-l-[3px] border-l-people px-3 py-2.5',
        marked ? 'border-people-bg bg-people-bg' : 'border-border bg-card'
      )}
    >
      <div className="text-sm font-medium text-people-ink">{levelLabel(level)}</div>
      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{level.meaning}</p>
      {marked && (
        <p className="mt-2">
          <Link
            to={LADDER_MARKER.href}
            className="text-xs font-medium text-people-ink underline underline-offset-2 hover:no-underline"
          >
            {LADDER_MARKER.label}
          </Link>
          <span className="block text-xs text-ink-soft">{LADDER_MARKER.note}</span>
        </p>
      )}
    </div>
  )
}

function BadgeTableRow({ row }: { row: BadgeRow }) {
  const { named = [] } = row.holders

  return (
    <TableRow>
      <TableHead scope="row" className="h-auto max-w-[15rem] whitespace-normal py-3 align-top">
        <span className="block text-sm font-medium text-foreground">{row.name}</span>
        {row.skillName !== null && (
          <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
            {BADGES_TABLE.skillPrefix}{' '}
            <Link
              to={BADGES_TABLE.skillHref}
              className="underline underline-offset-2 hover:no-underline"
            >
              {row.skillName}
            </Link>
          </span>
        )}
      </TableHead>

      <TableCell className="max-w-[16rem] whitespace-normal py-3 align-top text-sm text-ink-soft">
        {row.earnedBy}
      </TableCell>

      <TableCell className="max-w-[15rem] whitespace-normal py-3 align-top text-sm text-foreground">
        {row.unlocks}
      </TableCell>

      <TableCell className="max-w-[14rem] whitespace-normal py-3 align-top text-sm text-ink-soft">
        {row.reviewRights}
      </TableCell>

      <TableCell className="max-w-[14rem] whitespace-normal py-3 align-top">
        <span className="v-figure block text-sm tabular-nums text-foreground">
          {BADGES_TABLE.holdersLabel(row.holders.count)}
        </span>
        <span className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-xs text-muted-foreground">
          {named.map((holder, index) => (
            <span key={holder.id} className="inline-flex items-baseline gap-1.5">
              {index > 0 && <span aria-hidden>·</span>}
              <Link
                to={holder.href}
                className="text-ink-soft underline underline-offset-2 hover:text-foreground"
              >
                {holder.name}
              </Link>
              <span>{holder.level}</span>
            </span>
          ))}
          {row.holders.moreCount > 0 && (
            <span>{BADGES_TABLE.holdersMore(row.holders.moreCount)}</span>
          )}
        </span>
      </TableCell>

      <TableCell className="whitespace-normal py-3 align-top">
        {row.expires === null ? (
          <span className="text-sm text-muted-foreground">{BADGES_TABLE.noExpiry}</span>
        ) : (
          <StatusPill tone="review">{row.expires}</StatusPill>
        )}
      </TableCell>
    </TableRow>
  )
}

/**
 * 1.9 Badges — what a badge changes about allocation. The ladder states the
 * five levels PX reads, the table states what each badge is earned by and
 * what it unlocks, and the three panels below close the loop: the badge shows
 * up in the match, it is verified by records rather than claims, and the
 * version-bound ones lapse on a date the training calendar already knows.
 */
export default function BadgesPage() {
  return (
    <AppShell
      moduleLabel={BADGES_CHROME.moduleLabel}
      breadcrumb={BADGES_CHROME.breadcrumb}
      contextPill={BADGES_CHROME.contextPill}
      nav={resourcesNav}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{BADGES_COPY.title}</h1>
      {/* PX-W6.2 (AC-8): the demo badge — this surface renders seed content and stays network-quiet. */}
      <ConceptBadge />
        <p className="text-sm text-muted-foreground">{BADGES_COPY.lede}</p>
      </header>

      {/* 1. The ladder — five rungs, and where one person stands today. */}
      <section aria-labelledby="ladder-label" className="mt-5">
        <div id="ladder-label" className="flex items-center gap-1.5">
          <BadgeCheck className="h-3.5 w-3.5 text-people-ink" aria-hidden />
          <SectionLabel>{LADDER.label}</SectionLabel>
        </div>
        <p className="mt-1.5 max-w-4xl text-sm text-ink-soft">{LADDER.lede}</p>
        <ol className="mt-3 flex flex-wrap items-stretch gap-y-2">
          {LEVELS.map((level, index) => (
            <li key={level.key} className="flex flex-1 items-stretch">
              {index > 0 && (
                <ChevronRight
                  className="mx-1 mt-3 h-4 w-4 shrink-0 self-start text-people"
                  aria-hidden
                />
              )}
              <LadderStep level={level} marked={level.key === LADDER_MARKER.levelKey} />
            </li>
          ))}
        </ol>
      </section>

      <Separator className="mt-6" />

      {/* 2. The badges themselves. */}
      <section className="mt-5 rounded-lg border border-border bg-card" aria-label={BADGES_COPY.title}>
        <Table>
          <TableCaption className="sr-only">{BADGES_TABLE.caption}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col" className={cn(HEAD_CLASS, 'pl-3')}>
                {BADGES_TABLE.columns.badge}
              </TableHead>
              <TableHead scope="col" className={HEAD_CLASS}>
                {BADGES_TABLE.columns.earnedBy}
              </TableHead>
              <TableHead scope="col" className={cn(HEAD_CLASS, 'h-auto py-2 align-bottom')}>
                <span className="block">{BADGES_TABLE.columns.unlocks}</span>
                <Link
                  to={BADGES_TABLE.unlocksNote.href}
                  className="block font-medium normal-case tracking-normal text-accent-foreground underline underline-offset-2 hover:no-underline"
                >
                  {BADGES_TABLE.unlocksNote.label}
                </Link>
              </TableHead>
              <TableHead scope="col" className={HEAD_CLASS}>
                {BADGES_TABLE.columns.reviewRights}
              </TableHead>
              <TableHead scope="col" className={HEAD_CLASS}>
                {BADGES_TABLE.columns.holders}
              </TableHead>
              <TableHead scope="col" className={HEAD_CLASS}>
                {BADGES_TABLE.columns.expires}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {BADGE_ROWS.map((row) => (
              <BadgeTableRow key={row.key} row={row} />
            ))}
          </TableBody>
        </Table>
      </section>

      {/* 3. What the badge does downstream: allocation, verification, expiry. */}
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <section aria-labelledby="match-label" className="rounded-lg bg-accent px-4 py-4">
          <div id="match-label">
            <SectionLabel>{MATCH_PANEL.label}</SectionLabel>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-foreground">
            <Prose segments={MATCH_PANEL.prose} className="text-accent-foreground" />
          </p>
        </section>

        <section
          aria-labelledby="verification-label"
          className="rounded-lg border border-border bg-card px-4 py-4"
        >
          <div id="verification-label" className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            <SectionLabel>{VERIFICATION_PANEL.label}</SectionLabel>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-foreground">
            <Prose segments={VERIFICATION_PANEL.prose} />
          </p>
        </section>

        <section
          aria-labelledby="expiry-label"
          className="rounded-lg border border-border border-l-[3px] border-l-review bg-card px-4 py-4"
        >
          <div id="expiry-label" className="flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5 text-review" aria-hidden />
            <SectionLabel>{EXPIRY_PANEL.label}</SectionLabel>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-foreground">
            <Prose segments={EXPIRY_PANEL.prose} />
          </p>
        </section>
      </div>

      <DensityNote>{BADGES_COPY.densityNote}</DensityNote>
    </AppShell>
  )
}
